/**
 * Sound guard: no song may keep playing without a player the maker can see and stop.
 *
 * Every audible media element that starts playing is remembered — also after it leaves the page,
 * because a detached element can no longer be found in the document. Leaving a page stops them
 * all, and anything still audible without a visible player surfaces a Stop control.
 */
const audible = new Set<HTMLMediaElement>();
const listeners = new Set<() => void>();
let installed = false;
let ticking = 0;

function isAudible(media: HTMLMediaElement): boolean {
  return !media.paused && !media.ended && !media.muted && media.volume > 0;
}

/** A player the maker can see: native controls on screen, an open dialog, or a marked surface. */
function hasVisiblePlayer(media: HTMLMediaElement): boolean {
  if (!media.isConnected) return false;
  if (media.controls && media.getClientRects().length > 0) return true;
  return !!media.closest("dialog[open], [data-sound-surface]");
}

function notify() {
  for (const media of audible) if (media.paused || media.ended) audible.delete(media);
  listeners.forEach((listener) => listener());
  window.clearInterval(ticking);
  // Detached elements send no events the document can hear, so look again while anything plays.
  if (audible.size) ticking = window.setInterval(notify, 1000);
}

export function installSoundGuard(root: Document = document): void {
  if (installed) return;
  installed = true;
  // Media events do not bubble, but a capturing listener on the document still receives them.
  root.addEventListener(
    "play",
    (event) => {
      if (event.target instanceof HTMLMediaElement) audible.add(event.target);
      notify();
    },
    true,
  );
  for (const type of ["pause", "ended", "emptied", "volumechange"]) root.addEventListener(type, notify, true);
}

/** Audible playback with no visible way to stop it. */
export function straySound(): HTMLMediaElement[] {
  return [...audible].filter((media) => isAudible(media) && !hasVisiblePlayer(media));
}

/** Sound that is not a media element (Iris's streamed voice through Web Audio). */
const stoppers = new Set<() => void>();
export function registerSoundStopper(stop: () => void): () => void {
  stoppers.add(stop);
  return () => stoppers.delete(stop);
}

/** Stop everything audible; used when the maker leaves a page. Muted previews keep playing. */
export function stopAllSound(): void {
  for (const media of audible) if (isAudible(media)) media.pause();
  for (const stop of [...stoppers]) stop();
  notify();
}

export function stopStraySound(): void {
  straySound().forEach((media) => media.pause());
  notify();
}

export function subscribeSound(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
